import {
  Flex,
  Icon,
  SystemProps,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import React, { useEffect } from "react";
import { HiVideoCamera, HiVideoCameraSlash } from "react-icons/hi2";

import { useIsSmallScreen } from "../../../hooks/useIsSmallScreen";
import {
  InterviewImportDecisionReason,
  InterviewType,
  ScheduledInterviewListItemFragment,
} from "../../graphql";
import { disabledReasons, enabledReasons, optOutReasons } from "./constants";

type RecordingMessageProps = {
  scheduledInterview: Pick<
    ScheduledInterviewListItemFragment,
    | "isImported"
    | "isVideo"
    | "importDecision"
    | "importDecisionReason"
    | "interviewType"
  >;
} & SystemProps;

enum RecordingStatus {
  ENABLED = "ENABLED",
  DISABLED = "DISABLED",
}

/**
 * Renders a "no video" icon, "Recording disabled" text,
 * and an explanatory tooltip that is also mobile-friendly
 */
const RecordingMessage: React.FC<RecordingMessageProps> = ({
  scheduledInterview,
  ...styles
}) => {
  const isSmallScreen = useIsSmallScreen();
  const {
    isOpen: showingMobileTooltip,
    onOpen: showMobileTooltip,
    onClose: hideMobileTooltip,
  } = useDisclosure();

  /**
   * For mobile, clear tooltip after scroll or 2.5 seconds
   */
  useEffect(() => {
    if (!showingMobileTooltip) return;
    window.addEventListener("scroll", hideMobileTooltip, { once: true });
    const timeout = setTimeout(hideMobileTooltip, 2500);
    return () => {
      window.removeEventListener("scroll", hideMobileTooltip);
      clearTimeout(timeout);
    };
  }, [showingMobileTooltip]);

  const willRecord: RecordingStatus =
    scheduledInterview.isImported ||
    (scheduledInterview.isVideo === false && scheduledInterview.importDecision)
      ? RecordingStatus.ENABLED
      : RecordingStatus.DISABLED;

  if (
    scheduledInterview.interviewType === InterviewType.InPerson ||
    scheduledInterview.interviewType === InterviewType.Phone ||
    !scheduledInterview.interviewType
  )
    return null;

  const reasonDescriptions = {
    [RecordingStatus.ENABLED]: { ...enabledReasons },
    [RecordingStatus.DISABLED]: {
      ...optOutReasons,
      ...disabledReasons,
    },
  };

  const recordingDisabledReason =
    (scheduledInterview.importDecisionReason &&
      (
        reasonDescriptions[willRecord] as Record<
          InterviewImportDecisionReason,
          { shortDescription: string; longDescription: string }
        >
      )?.[scheduledInterview.importDecisionReason]?.longDescription) ||
    "Recording disabled because the interview is not configured to be recorded.";

  const getTextLabel = (): string => {
    const reason = scheduledInterview.importDecisionReason;
    if (willRecord === RecordingStatus.ENABLED) return "Recording on";
    if (
      willRecord === RecordingStatus.DISABLED &&
      reason &&
      reason in optOutReasons
    )
      return "Recording blocked";
    return "Recording off";
  };

  return (
    <Flex
      alignItems="center"
      color="gray.500"
      onClick={showMobileTooltip}
      {...styles}
    >
      <Icon
        as={
          willRecord === RecordingStatus.ENABLED
            ? HiVideoCamera
            : HiVideoCameraSlash
        }
        color={
          willRecord === RecordingStatus.ENABLED ? "green.500" : "gray.500"
        }
        boxSize="5"
      />
      <Tooltip
        label={recordingDisabledReason}
        isOpen={isSmallScreen ? showingMobileTooltip : undefined}
        maxW="240"
        placement={isSmallScreen ? "auto-end" : undefined}
      >
        <Text
          fontSize="sm"
          fontWeight="500"
          ml="2"
          lineHeight="5"
          data-testid="scheduled-interview-recording-disabled-text"
        >
          {getTextLabel()}
        </Text>
      </Tooltip>
    </Flex>
  );
};

export default RecordingMessage;
